/**
 * How a failed graphic is explained to the owner, in one place (ENG-1155).
 *
 * The worker persists a stable `errorCode` (GFX-xxxx) and a safe
 * `errorMessage` on the graphic row. Every surface that shows a failed graphic
 * (content library card, processing modal, Claire card and artifact panel,
 * batch review) reads it through `describeGraphicFailure`, so a safety block
 * gets the same heading and reason everywhere, and a toast is raised once
 * when one lands while the owner is watching.
 */
import { useEffect } from 'react';
import { toast } from 'sonner';

import {
  GraphicGenerationErrorCodes,
  GraphicGenerationErrorMessages,
  IMAGE_SAFETY_BLOCKED_ERROR_CODE,
  IMAGE_SAFETY_BLOCK_USER_MESSAGE,
} from './api/types';

export interface GraphicFailureFields {
  errorCode?: string | null;
  errorMessage?: string | null;
}

export interface GraphicFailureCopy {
  /** Short heading for the failed state. */
  title: string;
  /** The owner-safe reason to show under the heading. */
  message: string;
  /** The code to quote to support, when the worker recorded one. */
  code: string | null;
  /** True when the image provider's safety checks blocked the render. */
  isSafetyBlock: boolean;
}

export const GRAPHIC_SAFETY_BLOCK_TITLE = 'Blocked by image safety checks';

export function isGraphicSafetyBlock(errorCode?: string | null): boolean {
  return errorCode === GraphicGenerationErrorCodes.MODEL_SAFETY_BLOCKED;
}

/**
 * The heading, reason and code for a failed graphic. A safety block always
 * uses canonical copy, even if an older row has an unsafe provider message.
 */
export function describeGraphicFailure(
  graphic: GraphicFailureFields,
  fallbackMessage: string
): GraphicFailureCopy {
  const isSafetyBlock = isGraphicSafetyBlock(graphic.errorCode);
  return {
    title: isSafetyBlock
      ? GRAPHIC_SAFETY_BLOCK_TITLE
      : 'Graphic generation failed',
    message: isSafetyBlock
      ? GraphicGenerationErrorMessages[
          GraphicGenerationErrorCodes.MODEL_SAFETY_BLOCKED
        ]
      : graphic.errorMessage?.trim() || fallbackMessage,
    code: graphic.errorCode ?? null,
    isSafetyBlock,
  };
}

/**
 * A failure older than this is history, not news: reopening an old Claire
 * conversation must not toast about a graphic that failed last week.
 */
const RECENT_FAILURE_MS = 10 * 60 * 1000;

/** Graphics already toasted this session, across every surface showing them. */
const toastedGraphicIds = new Set<string>();

/**
 * Sonner's phone layout (600px and below) stretches a toast across the whole
 * screen. At the bottom that lands on Claire's composer, just as the owner is
 * told to tell Claire what to change, so on a phone the toast drops in from
 * the top instead, under the header (the Toaster's `mobileOffset`).
 */
const PHONE_TOAST_QUERY = '(max-width: 600px)';

function safetyToastPosition(): 'top-center' | 'bottom-right' {
  return typeof window !== 'undefined' &&
    window.matchMedia?.(PHONE_TOAST_QUERY).matches
    ? 'top-center'
    : 'bottom-right';
}

/**
 * One safety toast at a time. A block that lands while it is still showing
 * updates it to a count ("2 graphics blocked …") instead of stacking another
 * toast; once it closes, the next block starts a fresh one.
 */
const SAFETY_TOAST_ID = 'image-safety-block';
let blocksInToast = 0;

function showSafetyBlockToast({
  noun,
  message,
  pluralMessage,
  code,
}: {
  noun: 'graphic' | 'image';
  message: string;
  pluralMessage: string;
  code: string | null;
}): void {
  blocksInToast += 1;
  const count = blocksInToast;
  const codeSuffix = code ? ` Error code: ${code}.` : '';
  const reset = () => {
    blocksInToast = 0;
  };
  toast.error(
    count === 1
      ? GRAPHIC_SAFETY_BLOCK_TITLE
      : `${count} ${noun}s blocked by image safety checks`,
    {
      id: SAFETY_TOAST_ID,
      description: `${count === 1 ? message : pluralMessage}${codeSuffix}`,
      position: safetyToastPosition(),
      onDismiss: reset,
      onAutoClose: reset,
    }
  );
}

export interface SafetyBlockToastTarget extends GraphicFailureFields {
  id?: string | null;
  status?: string | null;
  updatedAt?: string | null;
}

/**
 * Raise a toast once when a graphic the owner is watching fails on a safety
 * block (bottom right; top on a phone). Only recent failures count, and each
 * graphic counts at most once per session, even when several surfaces poll the
 * same row.
 */
export function useGraphicSafetyBlockToast(
  graphic: SafetyBlockToastTarget | null | undefined
): void {
  const id = graphic?.id ?? null;
  const status = graphic?.status ?? null;
  const errorCode = graphic?.errorCode ?? null;
  const errorMessage = graphic?.errorMessage ?? null;
  const updatedAt = graphic?.updatedAt ?? null;

  useEffect(() => {
    if (!id || status !== 'failed' || !isGraphicSafetyBlock(errorCode)) return;
    if (toastedGraphicIds.has(id)) return;
    const failedAt = updatedAt ? Date.parse(updatedAt) : Number.NaN;
    if (!Number.isFinite(failedAt) || Date.now() - failedAt > RECENT_FAILURE_MS)
      return;
    toastedGraphicIds.add(id);
    const copy = describeGraphicFailure({ errorCode, errorMessage }, '');
    showSafetyBlockToast({
      noun: 'graphic',
      message: copy.message,
      pluralMessage:
        "The image service's safety checks blocked these graphics. Different wording or imagery may help.",
      code: copy.code,
    });
  }, [id, status, errorCode, errorMessage, updatedAt]);
}

/**
 * The same toast for a synchronous image tool (the website builder's
 * `generate_image`), which reports the block on its tool result rather than
 * on a graphic row. Several blocked images in one turn share one toast.
 */
export function notifyImageSafetyBlock(errorCode?: string | null): boolean {
  if (errorCode !== IMAGE_SAFETY_BLOCKED_ERROR_CODE) return false;
  showSafetyBlockToast({
    noun: 'image',
    message: IMAGE_SAFETY_BLOCK_USER_MESSAGE,
    pluralMessage:
      "The image service's safety checks blocked these images. Different wording or imagery may help.",
    code: null,
  });
  return true;
}

/** Test seam: forget which graphics have toasted and reset the count. */
export function resetGraphicSafetyBlockToasts(): void {
  toastedGraphicIds.clear();
  blocksInToast = 0;
}
